import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { getBrandsIndex } from "../lib/catalogueApi";
import { StoreLayout } from "./StoreLayout";
import { BrandMark } from "./BrandPage";

const letter = (n) => (/^[a-z]/i.test(n) ? n[0].toUpperCase() : "#");

// T55: A–Z index of brands that have a page right now (active + at least one sellable product)
export default function BrandsIndex() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { document.title = "Brands - Rhyte Step IT"; getBrandsIndex().then((d) => setRows(d.brands)).catch(() => setRows([])); }, []);
  const groups = useMemo(() => {
    const t = q.trim().toLowerCase(), out = {};
    (rows || []).filter((b) => !t || b.name.toLowerCase().includes(t)).forEach((b) => { (out[letter(b.name)] = out[letter(b.name)] || []).push(b); });
    return Object.entries(out).sort(([a], [b]) => (a === "#" ? 1 : b === "#" ? -1 : a.localeCompare(b)));
  }, [rows, q]);
  return (
    <StoreLayout>
      <div className="mx-auto max-w-6xl px-5 py-8" data-testid="brands-index">
        <nav className="mb-2 text-sm text-slate-500" aria-label="Breadcrumb"><Link to="/" className="hover:text-[var(--brand-link)]">Home</Link> / Brands</nav>
        <h1 className="rs-h1 mb-2 text-[var(--brand-primary)]">Shop by brand</h1>
        <p className="mb-6 text-slate-600" data-testid="brands-count">{rows ? `${rows.length} brands with products for sale, prices including GST.` : "\u00a0"}</p>
        <label className="relative mb-6 block max-w-md">
          <span className="sr-only">Search brands</span>
          <Search size={16} aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search brands" data-testid="brands-search"
            className="w-full rounded-full border border-slate-300 bg-white py-2.5 pl-9 pr-4 text-sm focus:border-[var(--brand-link)] focus:outline-none" />
        </label>
        {groups.length > 1 && !q && (
          <nav aria-label="Brands A to Z" className="mb-6 flex flex-wrap gap-1" data-testid="brands-az">
            {groups.map(([l]) => <a key={l} href={`#brands-${l}`} className="rounded-md px-2 py-1 text-sm font-semibold text-slate-600 hover:bg-slate-100">{l}</a>)}
          </nav>)}
        {!rows ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6" aria-hidden="true">
            {Array.from({ length: 18 }).map((_, i) => <div key={i} className="h-[104px] animate-pulse rounded-xl bg-slate-100 motion-reduce:animate-none" />)}
          </div>
        ) : !groups.length ? <p className="py-10 text-slate-500" data-testid="brands-empty">No brands match “{q}”.</p> : groups.map(([l, bs]) => (
          <section key={l} id={`brands-${l}`} className="mb-8 scroll-mt-28" aria-labelledby={`brands-h-${l}`}>
            <h2 id={`brands-h-${l}`} className="mb-3 text-lg font-bold text-slate-800">{l}</h2>
            <ul className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
              {bs.map((b) => (
                <li key={b.slug}>
                  <Link to={`/brands/${b.slug}`} data-testid={`brand-tile-${b.slug}`}
                    className="group flex flex-col gap-2 rounded-xl border border-slate-200 bg-white p-2 transition-[border-color,box-shadow] duration-150 hover:border-[var(--brand-link)] hover:shadow-sm">
                    <BrandMark name={b.name} logoId={b.logo_media_id} size="sm" testid={`brand-tile-mark-${b.slug}`} />
                    <span className="truncate px-1 text-sm font-semibold text-slate-800 group-hover:text-[var(--brand-link)]">{b.name}
                      <span className="ml-1 font-normal text-slate-400">{b.count.toLocaleString()}</span></span>
                  </Link>
                </li>))}
            </ul>
          </section>))}
      </div>
    </StoreLayout>
  );
}
